import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, Check, Share2, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Pop-up that lets agents share a finale-video / reel link with anyone (no login required).
 * The link points at the public /api/uploads/{filename} endpoint, which 302-redirects to a
 * 24h-valid presigned R2 URL — so the recipient can view AND download in their own browser.
 */
export default function ShareLinkDialog({ open, onOpenChange, url, title = "Deel deze video", subject = "Vastgoed video" }) {
    const [copied, setCopied] = useState(false);
    const fullUrl = url ? (url.startsWith("http") ? url : `${BACKEND}${url}`) : "";

    useEffect(() => {
        if (!open) setCopied(false);
    }, [open]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(fullUrl);
            setCopied(true);
            toast.success("Link gekopieerd");
            setTimeout(() => setCopied(false), 2500);
        } catch {
            toast.error("Kopiëren mislukt — kopieer handmatig");
        }
    };

    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`Bekijk hier de video:\n\n${fullUrl}\n\n— Verstuurd via Vastgoed Social Factory`)}`;
    const whatsapp = `https://wa.me/?text=${encodeURIComponent(`${subject}: ${fullUrl}`)}`;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-[#FFFFFF] border-zinc-200 text-zinc-950 max-w-lg" data-testid="share-link-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
                        <Share2 className="h-5 w-5 text-[#E5A93D]" /> {title}
                    </DialogTitle>
                    <p className="text-sm text-zinc-500">
                        De ontvanger kan de video bekijken én downloaden in zijn browser, zonder in te loggen. De link blijft 24 uur geldig.
                    </p>
                </DialogHeader>

                <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Deelbare link</div>
                    <div className="flex items-center gap-2">
                        <input
                            readOnly
                            value={fullUrl}
                            onClick={(e) => e.target.select()}
                            className="flex-1 h-11 px-3 bg-[#F4F4F5] border border-zinc-200 rounded-sm text-xs text-zinc-800 font-mono focus:outline-none focus:border-[#E5A93D]/40"
                            data-testid="share-link-input"
                        />
                        <button
                            onClick={copy}
                            className={`inline-flex items-center gap-2 h-11 px-4 rounded-sm text-sm font-bold transition active:scale-[0.98] ${copied ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-700" : "bg-[#E5A93D] hover:bg-[#C8922C] text-black"}`}
                            data-testid="share-link-copy"
                        >
                            {copied ? (<><Check className="h-4 w-4" /> Gekopieerd</>) : (<><Copy className="h-4 w-4" /> Kopieer</>)}
                        </button>
                    </div>
                </div>

                <div className="mt-4 pt-4 border-t border-zinc-100">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">Of deel via</div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <a
                            href={whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 h-10 px-4 bg-[#25D366]/10 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/20 rounded-sm text-sm font-medium transition"
                            data-testid="share-whatsapp"
                        >
                            <MessageCircle className="h-4 w-4" /> WhatsApp
                        </a>
                        <a
                            href={mailto}
                            className="inline-flex items-center gap-2 h-10 px-4 bg-zinc-100 border border-zinc-200 text-zinc-800 hover:bg-zinc-100 rounded-sm text-sm font-medium transition"
                            data-testid="share-email"
                        >
                            <Mail className="h-4 w-4" /> E-mail
                        </a>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
